import { useMemo } from "react"
import { useNavigate } from "react-router"
import { BookOpen, Clock, ChevronRight, CheckCircle2, BarChart2, Zap } from "lucide-react"
import { Sidebar } from "../components/ui/sidebar"
import ProtectedRoute from "../components/ProtectedRoute"
import { MICRO_COURSE_CATALOG } from "@/data/micro-courses"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"

const PATH_LABELS = {
  broad_stem_tech:        "STEM & Tech",
  broad_health_medicine:  "Health & Medicine",
  broad_business_finance: "Business & Finance",
  broad_creative_arts:    "Creative Arts",
  broad_humanities_law:   "Humanities & Law",
  broad_law_governance:   "Law & Governance",
  broad_law_social:       "Law & Social",
}

const DIFFICULTY_LABEL = ["", "Beginner", "Intermediate", "Advanced"]

function CourseCard({ course, completed, inProgress, onClick }) {
  const totalModules = course.modules.length
  const totalQuestions = course.modules.reduce((s, m) => s + m.questions.length, 0)
  const completedModules = completed.length
  const progress = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0
  const isDone = progress === 100
  const isStarted = completedModules > 0

  return (
    <div
      onClick={onClick}
      className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md hover:border-indigo-200 transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold bg-indigo-50 text-indigo-600 border border-indigo-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
              {PATH_LABELS[course.broadPathId] ?? course.broadPathId}
            </span>
            <span className="text-[10px] text-slate-400 font-medium">
              {DIFFICULTY_LABEL[course.difficulty] ?? ""}
            </span>
          </div>
          <h3 className="font-black text-slate-900 text-base leading-snug">{course.title}</h3>
        </div>
        {isDone && <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />}
      </div>

      <p className="text-sm text-slate-500 leading-relaxed mb-4 line-clamp-2">{course.description}</p>

      <div className="flex items-center gap-4 text-xs text-slate-400 mb-4">
        <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" />{totalModules} modules</span>
        <span className="flex items-center gap-1"><BarChart2 className="w-3.5 h-3.5" />{totalQuestions} questions</span>
        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{course.estimatedMinutes} min</span>
      </div>

      {isStarted && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-slate-500 font-medium">{progress}% complete</span>
            <span className="text-xs text-slate-400">{completedModules}/{totalModules} modules</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${isDone ? "bg-emerald-500" : "bg-indigo-500"}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
          isDone    ? "bg-emerald-50 text-emerald-600" :
          isStarted ? "bg-amber-50 text-amber-600" :
                      "bg-slate-50 text-slate-500"
        }`}>
          {isDone ? "Completed" : isStarted ? "In Progress" : "Not Started"}
        </span>
        <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 group-hover:gap-2.5 transition-all">
          {isDone ? "Review" : isStarted ? "Continue" : "Start"}
          <ChevronRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </div>
  )
}

export default function LearningResourcesPage() {
  const navigate = useNavigate()

  const { selectedPaths, journeyRecords } = useMemo(() => {
    const currentUser = AuthStore.getState().currentUser
    const students = StorageService.read("students") ?? []
    const student = students.find(s => s.userId === currentUser?._id || s.email === currentUser?.email) ?? students[0]
    const allJourneys = StorageService.read("journeyRecords") ?? []
    const myJourneys = allJourneys.filter(j => j.userId === currentUser?._id || j.userId === student?.userId)
    const completedByPath = {}
    myJourneys.forEach(j => { completedByPath[j.courseId ?? j.pathId] = j.completedModuleIds ?? [] })
    return {
      selectedPaths: student?.selectedBroadPaths ?? [],
      journeyRecords: completedByPath,
    }
  }, [])

  const courses = useMemo(() => {
    const matched = MICRO_COURSE_CATALOG.filter(c => selectedPaths.includes(c.broadPathId))
    const other   = MICRO_COURSE_CATALOG.filter(c => !selectedPaths.includes(c.broadPathId))
    return [...matched, ...other]
  }, [selectedPaths])

  const stats = useMemo(() => {
    const total = courses.length
    const started = courses.filter(c => (journeyRecords[c.id]?.length ?? 0) > 0).length
    const done    = courses.filter(c => {
      const completed = journeyRecords[c.id]?.length ?? 0
      return completed >= c.modules.length && c.modules.length > 0
    }).length
    return { total, started, done }
  }, [courses, journeyRecords])

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />
        <main className="flex-1 lg:ml-64 p-4 md:p-8 overflow-y-auto">
          <div className="max-w-5xl mx-auto space-y-6">

            {/* Header */}
            <div>
              <h1 className="text-3xl font-black text-slate-900">Learning Resources</h1>
              <p className="text-slate-500 text-sm mt-1">
                {selectedPaths.length > 0
                  ? `Courses matched to your career paths · ${courses.length} available`
                  : `${courses.length} courses available · complete your assessment to get personalised suggestions`}
              </p>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Total Courses", value: stats.total, icon: BookOpen, color: "text-indigo-600", bg: "bg-indigo-50" },
                { label: "In Progress",   value: stats.started - stats.done, icon: Zap,      color: "text-amber-600",  bg: "bg-amber-50"  },
                { label: "Completed",     value: stats.done,   icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
              ].map(({ label, value, icon: Icon, color, bg }) => (
                <div key={label} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
                    <Icon className={`w-5 h-5 ${color}`} />
                  </div>
                  <div>
                    <p className="text-xl font-black text-slate-900">{Math.max(0, value)}</p>
                    <p className="text-xs text-slate-400 font-medium">{label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Course grid */}
            {selectedPaths.length > 0 && (
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Your Paths</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {courses
                    .filter(c => selectedPaths.includes(c.broadPathId))
                    .map(c => (
                      <CourseCard
                        key={c.id}
                        course={c}
                        completed={journeyRecords[c.id] ?? []}
                        onClick={() => navigate(`/course/${c.id}`)}
                      />
                    ))}
                </div>
              </div>
            )}

            {courses.filter(c => !selectedPaths.includes(c.broadPathId)).length > 0 && (
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">
                  {selectedPaths.length > 0 ? "Explore Other Paths" : "All Courses"}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {courses
                    .filter(c => !selectedPaths.includes(c.broadPathId))
                    .map(c => (
                      <CourseCard
                        key={c.id}
                        course={c}
                        completed={journeyRecords[c.id] ?? []}
                        onClick={() => navigate(`/course/${c.id}`)}
                      />
                    ))}
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </ProtectedRoute>
  )
}
